/* eslint-disable */
import React, { useState, useCallback, useRef } from 'react';
import { Music, Search, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import api from '../lib/api';
import { decodeYoutubeTitle } from '../lib/sanitizeText';

/**
 * Customer-facing "Request a Song" modal.
 *
 * Flow:
 *  1. Customer taps the floating Music button on the menu page.
 *  2. Types a query → debounced YouTube search via the backend proxy
 *     (`GET /api/youtube/search`), which keeps the API key server-side.
 *  3. Taps a result → submits via `POST /api/music-queue` with the venue's
 *     table_id + guest_id so staff can match faces to requests.
 *  4. Backend handles throttle (1 per 10 min) + profanity filter; we just
 *     toast the result and close.
 */
export default function MusicRequestModal({ open, onClose, tableId, guestId, requesterName }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);

  const doSearch = useCallback(async (query) => {
    const term = (query || '').trim();
    if (term.length < 2) { setResults([]); return; }
    setSearching(true); setError(null);
    try {
      const { data } = await api.get(`/youtube/search?q=${encodeURIComponent(term)}`);
      setResults(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err.response?.status === 503) {
        setError('Song search is temporarily unavailable. Please ask staff.');
      } else {
        setError('Search failed. Try again.');
      }
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const onQueryChange = (e) => {
    const v = e.target.value;
    setQ(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(v), 350);
  };

  const submit = async (item) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post('/music-queue', {
        video_id: item.video_id,
        title: item.title,
        channel_title: item.channel_title,
        thumbnail_url: item.thumbnail_url,
        table_id: tableId,
        guest_id: guestId,
        requester_name: requesterName,
      });
      toast.success(data.message || `Your song is #${data.queue_position} in the queue`);
      setQ(''); setResults([]);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Submit failed';
      if (err.response?.status === 429) {
        toast.error(detail);
      } else {
        toast.error(detail);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-bg-primary border border-gold/20 max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-serif text-white flex items-center gap-2">
            <Music className="w-5 h-5 text-gold" /> Request a Song
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              value={q}
              onChange={onQueryChange}
              placeholder="Artist, track, vibe…"
              className="pl-9 bg-transparent border-0 border-b border-white/15 rounded-none focus:border-gold focus:ring-0 text-white h-11 font-sans text-sm placeholder:text-zinc-700"
              autoFocus
              data-testid="music-search-input"
            />
            {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gold animate-spin" />}
          </div>
          {error && (
            <p className="text-xs text-red-300 font-sans bg-red-500/10 border border-red-500/30 rounded-sm px-3 py-2">{error}</p>
          )}
          <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-sans">
            One request per guest every 10 minutes
          </p>
          <div className="max-h-[58vh] overflow-y-auto -mx-2 px-2 space-y-1.5" data-testid="music-search-results">
            {results.map((it) => (
              <button
                key={it.video_id}
                type="button"
                disabled={submitting}
                onClick={() => submit(it)}
                className="w-full flex items-center gap-3 p-2 rounded-sm border border-white/5 bg-white/[0.02] hover:bg-gold/10 hover:border-gold/40 transition-all text-left disabled:opacity-50 active:scale-[0.98]"
                data-testid={`music-result-${it.video_id}`}
              >
                {it.thumbnail_url ? (
                  <img src={it.thumbnail_url} alt="" className="w-14 h-14 rounded-sm object-cover flex-shrink-0" />
                ) : (
                  <div className="w-14 h-14 rounded-sm bg-white/5 flex items-center justify-center flex-shrink-0">
                    <Music className="w-5 h-5 text-zinc-600" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-serif text-white text-sm leading-tight line-clamp-2">{decodeYoutubeTitle(it.title)}</p>
                  <p className="text-[10.5px] text-zinc-500 font-sans truncate mt-0.5">{it.channel_title}</p>
                </div>
              </button>
            ))}
            {!searching && q.length >= 2 && results.length === 0 && !error && (
              <p className="text-xs text-zinc-500 font-sans italic text-center py-4">No results — try another search.</p>
            )}
            {q.length < 2 && !searching && !error && (
              <p className="text-xs text-zinc-500 font-sans italic text-center py-4">Type at least 2 letters to search.</p>
            )}
          </div>
          <Button onClick={() => { setQ(''); setResults([]); onClose(); }} className="w-full bg-transparent border border-white/20 text-zinc-400 hover:bg-white/5 rounded-sm font-sans text-sm" data-testid="music-modal-close">
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
